import CanvasImageWidget from '../widget/image-widget'
import CanvasTextWidget from '../widget/text-widget'
import CanvasTextV4Widget from '../widget/text-v4-widget'
import CanvasSVGWidget from '../widget/svg-widget'
import CanvasNavigationWidget from '../widget/navigation-widget'
import CanvasDotNavigationWidget from '../widget/dot-navigation-widget'
import CanvasVideoWidget from '../widget/video-widget'
import CanvasCodesnippetWidget from '../widget/codesnippet-widget'
import FauxBorderFrame from '../faux-border'
import RenderingMath from './RenderingMath'
import RichPlaceholderCompiler from './RichPlaceholderCompiler'
import IdService from 'id-service'

function getCanvasWidgetClassByType(type) {
  switch (type) {
    case 'text':
      return CanvasTextWidget
    case 'textV4':
      return CanvasTextV4Widget
    case 'image':
      return CanvasImageWidget
    case 'svg':
      return CanvasSVGWidget
    case 'video':
    case 'vast':
      return CanvasVideoWidget
    case 'navigation':
      return CanvasNavigationWidget
    case 'dotNavigation':
      return CanvasDotNavigationWidget
    case 'codesnippet':
      return CanvasCodesnippetWidget
    default:
      //FIXME: create a fallback for unknown widget type
      return CanvasTextWidget
  }
}

function parseElementStyle(blueprint) {
  return {
    position: 'relative',
    overflow: 'hidden',
    width: `${blueprint.width}px`,
    height: `${blueprint.height}px`,
    background: blueprint.background,
  }
}

function isWidgetWithinAdDisplay(widget, blueprint) {
  const { width, height } = blueprint
  const { width: widgetWidth, height: widgetHeight, x, y } = widget.layout

  return (
    RenderingMath.isOverlapped({ left: x, right: x + widgetWidth }, { left: 0, right: width }) &&
    RenderingMath.isOverlapped({ left: y, right: y + widgetHeight }, { left: 0, right: height })
  )
}

function escapeLinkAttribute(rawString) {
  return rawString.replace(/[<>]/g, substr => {
    return (
      {
        '<': '&lt;',
        '>': '&gt;',
      }[substr] || substr
    )
  })
}

// Given a blueprint, iterate thru all widgetGroups and widgets to gather list of fonts
function calculateInUseFontFaces(blueprint) {
  const result = new Set()

  blueprint.widgetGroups.forEach(wg => {
    wg.widgets.forEach(w => {
      if (w.baseStyle && w.baseStyle['font-family']) {
        // remove fallback e.g. "Arial" as in "Roboto, Arial" declaration
        result.add(w.baseStyle['font-family'].replace(/,.*/, ''))
      }
    })
  })

  return result
}

function generateCustomFontFaceStyle(fontFaceSet, inUseSet) {
  if (!fontFaceSet || fontFaceSet.length === 0) {
    return ''
  }

  return fontFaceSet
    .filter(f => inUseSet.has(f.family))
    .map(f => {
      return `@font-face {
      font-family: "${f.family}";
      src: url("${f.src}")
    }`
    })
    .join('\n')
}

export default {
  functional: true,
  props: {
    blueprint: {
      type: Object,
      required: true,
    },
    excludeOutofboundContent: Boolean,
    clipImageWidgets: Boolean,
    quickStart: {
      type: Object,
      default: undefined,
    },
  },
  render($h) {
    // Inserted a script block containing font-face declaration
    //  of custom fonts currently in-use
    const inUseSet = calculateInUseFontFaces(this.blueprint)
    const fontFaces = $h('style', {
      domProps: {
        innerHTML: generateCustomFontFaceStyle(this.blueprint.fontFaceSet, inUseSet),
      },
    })

    // list of animations to be used to create adacadoAnimations style tag
    let animationsSet = []
    // svg specific animations are stored separately as they act on the svg element itself and not the containing div
    let svgAnimationsSet = []
    // widgets to be hidden before animation starts
    let widgetsVisibilityBeforeAnimation = []

    const contentCompiler = new RichPlaceholderCompiler({
      mode: 'placeholder',
    })

    // Template Background (most bottom layer) must be clickable (ie all points in an ad must be clickable)
    const backgroundClickableArea = $h('a', {
      attrs: {
        href: escapeLinkAttribute(
          // blueprint.backgroundHref has been deprecated due to
          // https://git.adacado.com/ui/template-selector-page/issues/68
          contentCompiler.compile(
            {
              displayName: 'Background Link',
              name: 'backgroundLink',
              type: 'url',
              truncate: null,
              defaultValue: this.blueprint?.defaultClickThroughURL || '',
              inventoryIndex: 0,
              codeType: 'attribute',
            },
            {
              isURL: true,
            }
          )
        ),
        target: '_blank',
      },
      style: {
        width: `${this.blueprint.width}px`,
        height: `${this.blueprint.height}px`,
        position: 'absolute',
        'z-index': 0,
        display: 'block',
      },
    })

    // Flatten widgetGroup array for html rendering
    // Mar 12, 2021 - for now we'll render things the same way, that is widgetGroups DO NOT have any rendered affects
    // Mar 25, 2021 - Multiview rendering - render each widgetGroup as an html blob of elements, NOT a single element with nested children
    let widgetElements = []
    this.blueprint.widgetGroups.forEach((wg, i) => {
      widgetElements.push(
        ...wg.widgets
          .filter(
            widget =>
              !this.excludeOutofboundContent || isWidgetWithinAdDisplay(widget, this.blueprint) || widget.animations
          )
          .map(widget => {
            const widgetSettings = {
              props: {
                widget,
                contentCompiler,
                isHyperlinkEnabled: true, // This seems to be the only place in codebase that sets this key - when is it ever set to false?  FIXME - refactor
                zModifier: 0,
                inventoryIndex: i,
              },
            }

            if (this.clipImageWidgets && widget.type === 'image') {
              widgetSettings.props.clipBox = {
                width: this.blueprint.width,
                height: this.blueprint.height,
              }
            }

            // Give this widget a unique id that we might use later
            // It's not possible to create an inline-css rule to implement css :hover,
            // so we need to create a css rule and have it apply to a unique id
            widgetSettings.props.widgetId = IdService.generate()

            // Decorate html with addOns - this may be just simple indicators/hooks for clientside rendering to use
            // to full html/css/javascript that is attached to this widget.  We want to do this before the executing
            // the Vue createElement (aka $h) so that we don't mess with Vue's way of doing things (be declarative as
            // much as possible)
            // Perry Todo - do proper implementation of Add-On decorators HERE, these decorators should only be added when
            // we transform a blueprint to html

            // move animation object into animations array and remove animation key
            if (!widget.animations && widget.animation) {
              widget.animation.version = 3
              widget.animation.id = IdService.generate()
              widget.animation.type = widget.animation.animationStates[0].type
              // Animation-fill-mode 'both' does not work with multiple animations for same properties.
              // This is because fill mode 'both' tries to keep the styling of the beginning and last states of a keyframe
              // but if there are multiple keyframes, the animation needs to decide which beginning and last states to use,
              // therefore it ends up using just the last animation keyframe's states. 'forwards' works as it only looks at the
              // the last state of each keyframe, which it can determine easily for each animation in a multi animation set.
              widget.animation.animationSettings['animation-fill-mode'] = 'forwards'
              widget.animation.animationStates[0].layout = {
                ...widget.animation.animationStates[0].layout,
                width: widget.layout.width,
                height: widget.layout.height,
              }
              widget.animation.animationStates.push({
                layout: {
                  x: widget.layout.x,
                  y: widget.layout.y,
                  width: widget.layout.width,
                  height: widget.layout.height,
                },
              })
              widget.animations = [widget.animation]
              delete widget.animation
            }

            // generate widget animations
            if (widget?.animations?.length) {
              let widgetAnimations = []
              let widgetAnimationKeyFrames = []
              let svgWidgetAnimations = []
              let svgWidgetAnimationKeyFrames = []
              widget.animations.forEach(a => {
                // generate unique animation name (this will be keyed to a single widget)
                const animationName = IdService.generate()
                const { animationSettings, animationStates } = a

                // generate animation text
                const animationTextContent = `
                ${animationName} ${animationSettings['animation-duration']}s ${
                  animationSettings['animation-iteration-count']
                    ? animationSettings['animation-iteration-count']
                    : 'infinite'
                } ${animationSettings['animation-direction']} ${animationSettings['animation-timing-function']} ${
                  animationSettings['animation-fill-mode']
                } ${animationSettings['animation-delay']}s
                `

                // generate keyframe for animations
                let animationKeyFrameTextContent
                switch (true) {
                  case a.type === 'move' || a.type === 'resize':
                    animationKeyFrameTextContent = `
                    @keyframes ${animationName} {
                      from {
                        left: ${animationStates[0].layout.x}px;
                        top: ${animationStates[0].layout.y}px;
                        width: ${animationStates[0].layout.width}px;
                        height: ${animationStates[0].layout.height}px;
                      }
                      to {
                        left: ${animationStates[1].layout.x}px;
                        top: ${animationStates[1].layout.y}px;
                        width: ${animationStates[1].layout.width}px;
                        height: ${animationStates[1].layout.height}px;
                      }
                    }
                  `
                    break
                  case a.type === 'transition':
                    animationKeyFrameTextContent = `
                  @keyframes ${animationName} {
                    from {
                      ${a.targetStyle.name}: ${animationStates[0].transition.value}${
                      a.targetStyle.unit ? a.targetStyle.unit : ''
                    };
                    }
                    to {
                      ${a.targetStyle.name}: ${animationStates[1].transition.value}${
                      a.targetStyle.unit ? a.targetStyle.unit : ''
                    };
                    }
                  }
                  `
                    break
                  case a.type === 'rotation':
                    animationKeyFrameTextContent = `
                  @keyframes ${animationName} {
                    from {
                      transform: rotate(${animationStates[0].rotation.value}deg) rotateY(${animationStates[0].horizontal.value}deg) rotateX(${animationStates[0].vertical.value}deg);
                    }
                    to {
                      transform: rotate(${animationStates[1].rotation.value}deg) rotateY(${animationStates[1].horizontal.value}deg) rotateX(${animationStates[1].vertical.value}deg);
                    }
                  }
                  `
                    break
                }
                // here we check for svg specific style animations (fill, stroke, stroke-width), which need to be
                // separated so that they do not interfere with their parent div's animations
                if (
                  a.type === 'transition' &&
                  (a.targetStyle.name === 'fill' ||
                    a.targetStyle.name === 'stroke-width' ||
                    a.targetStyle.name === 'stroke')
                ) {
                  svgWidgetAnimations.push(animationTextContent)
                  svgWidgetAnimationKeyFrames.push(animationKeyFrameTextContent)
                } else {
                  widgetAnimations.push(animationTextContent)
                  widgetAnimationKeyFrames.push(animationKeyFrameTextContent)
                }
              })
              // combine animations into one animation css
              animationsSet.push(`
                .${widget.id}_class {
                  animation: ${widgetAnimations.join(',\n')};
                }
                ${widgetAnimationKeyFrames.join('\n')}
              `)
              svgAnimationsSet.push(`
                .${widget.id}_svgClass {
                  animation: ${svgWidgetAnimations.join(',\n')};
                }
                ${svgWidgetAnimationKeyFrames.join('\n')}
              `)
              // pass animation hash class name for widget to apply as class
              widgetSettings.props.animationClass = `${widget.id}_class`
              widgetSettings.props.svgAnimationClass = `${widget.id}_svgClass`
            }

            // Initial transformation of blueprint+settings to html
            widgetSettings.props.quickStart = this.quickStart
            return $h(getCanvasWidgetClassByType(widget.type), widgetSettings)
          })
          // Vue render function - inject html as slot content
          // https://vuejs.org/v2/guide/render-function.html#Slots
          .concat(this.$slots.default)
      )
    })

    const fauxBorder = $h(FauxBorderFrame, {
      props: {
        className: 'faux-border',
        width: this.blueprint.width,
        height: this.blueprint.height,
        borderColor: this.blueprint.borderColor,
        borderWidth: this.blueprint.borderWidth,
        // We can assert there will never be > 999 widgetGroups, or even >999 widgets..., and can be
        // certain that at 1000 the faux border will always be on top of all other visual elements
        zIndex: 1000,
        pivot: { x: 0, y: 0 },
      },
    })

    let elements = [fontFaces, backgroundClickableArea, ...widgetElements, fauxBorder]

    if (animationsSet.length || svgAnimationsSet.length) {
      // generate style tag for list of animations
      elements.push(
        $h('style', {
          attrs: {
            class: 'adacadoAnimation',
          },
          domProps: {
            innerHTML: animationsSet.join('\n') + svgAnimationsSet.join('\n'),
          },
        })
      )
      // generate style tag for hiding animated widgets before animations begin
      elements.push(
        $h('style', {
          domProps: {
            innerHTML: widgetsVisibilityBeforeAnimation.join('\n'),
          },
        })
      )
    }

    return $h(
      'div',
      {
        style: parseElementStyle(this.blueprint),
      },
      elements
    )
  },
}
